// FILE: src/pages/Orders.jsx
import React, { useState } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import Card from '../components/ui/Card';
import DataTable from '../components/ui/DataTable';
import ChartCard from '../components/ui/ChartCard';

const Orders = () => {
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Sample orders data
  const ordersData = [
    {
      id: '#ORD-001',
      customer: 'John Smith',
      email: 'john.smith@email.com',
      date: '2024-01-15',
      status: 'delivered',
      total: 245.99,
      items: 3,
      paymentMethod: 'Credit Card'
    },
    {
      id: '#ORD-002',
      customer: 'Sarah Johnson',
      email: 'sarah.j@email.com',
      date: '2024-01-14',
      status: 'processing',
      total: 189.50,
      items: 2,
      paymentMethod: 'PayPal'
    },
    {
      id: '#ORD-003',
      customer: 'Mike Davis',
      email: 'mike.davis@email.com',
      date: '2024-01-14',
      status: 'shipped',
      total: 399.99,
      items: 1,
      paymentMethod: 'Credit Card'
    },
    {
      id: '#ORD-004',
      customer: 'Emily Brown',
      email: 'emily.brown@email.com',
      date: '2024-01-13',
      status: 'pending',
      total: 156.75,
      items: 4,
      paymentMethod: 'Bank Transfer'
    },
    {
      id: '#ORD-005',
      customer: 'David Wilson',
      email: 'david.w@email.com',
      date: '2024-01-13',
      status: 'cancelled',
      total: 89.99,
      items: 1,
      paymentMethod: 'Credit Card'
    },
    {
      id: '#ORD-006',
      customer: 'Lisa Anderson',
      email: 'lisa.anderson@email.com',
      date: '2024-01-12',
      status: 'delivered',
      total: 567.80,
      items: 6,
      paymentMethod: 'Credit Card'
    }
  ];

  // Order stats
  const orderStats = [
    { title: 'Total Orders', value: '1,234', change: '+12%', trend: 'up', color: 'bg-blue-500' },
    { title: 'Pending Orders', value: '23', change: '-5%', trend: 'down', color: 'bg-yellow-500' },
    { title: 'Processing', value: '45', change: '+8%', trend: 'up', color: 'bg-purple-500' },
    { title: 'Revenue', value: '$45,678', change: '+15%', trend: 'up', color: 'bg-green-500' }
  ];

  // Chart data for order trends
  const orderTrendData = [
    { name: 'Jan', orders: 156, revenue: 15600 },
    { name: 'Feb', orders: 189, revenue: 18900 },
    { name: 'Mar', orders: 234, revenue: 23400 },
    { name: 'Apr', orders: 198, revenue: 19800 },
    { name: 'May', orders: 267, revenue: 26700 },
    { name: 'Jun', orders: 289, revenue: 28900 },
    { name: 'Jul', orders: 234, revenue: 23400 }
  ];

  // Order status distribution
  const statusDistribution = [
    { name: 'Delivered', value: 567 },
    { name: 'Processing', value: 123 },
    { name: 'Shipped', value: 89 },
    { name: 'Pending', value: 45 },
    { name: 'Cancelled', value: 23 }
  ];

  const getStatusBadge = (status) => {
    const statusClasses = {
      delivered: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      processing: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      shipped: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
      cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusClasses[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const columns = [
    {
      key: 'id',
      title: 'Order ID',
      render: (value) => <span className="font-medium text-blue-600">{value}</span>
    },
    {
      key: 'customer',
      title: 'Customer',
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-white">{value}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{row.email}</div>
        </div>
      )
    },
    {
      key: 'date',
      title: 'Date',
      render: (value) => new Date(value).toLocaleDateString()
    },
    {
      key: 'status',
      title: 'Status',
      render: (value) => getStatusBadge(value)
    },
    {
      key: 'items',
      title: 'Items',
      render: (value) => <span className="text-gray-600 dark:text-gray-300">{value} items</span>
    },
    {
      key: 'total',
      title: 'Total',
      render: (value) => <span className="font-medium">${value}</span>
    },
    {
      key: 'paymentMethod',
      title: 'Payment',
      render: (value) => <span className="text-sm text-gray-500 dark:text-gray-400">{value}</span>
    }
  ];

  const filteredOrders = ordersData.filter(order => {
    const matchesStatus = filterStatus === 'all' || order.status === filterStatus;
    const matchesSearch = order.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.id.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  return (
    <div>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Orders</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Manage and track all your orders
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors">
              Export Orders
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {orderStats.map((stat, index) => (
            <Card key={index} className="relative overflow-hidden">
              <div className="flex items-center">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                  <p className={`text-sm ${stat.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                    {stat.change} from last month
                  </p>
                </div>
                <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Order Trends */}
          <ChartCard 
            title="Order Trends" 
            subtitle="Monthly order volume and revenue"
            chartType="line"
            data={orderTrendData}
            dataKey={['orders', 'revenue']}
            xAxisKey="name"
            colors={['#3B82F6', '#10B981']}
          />
          
          {/* Order Status Distribution */}
          <ChartCard 
            title="Order Status Distribution" 
            subtitle="Current order status breakdown"
            chartType="donut"
            data={statusDistribution}
            dataKey="value"
            colors={['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444']}
          />
        </div>

        {/* Orders Table */}
        <Card title="Recent Orders" subtitle="Latest order activity">
          {/* Filters */}
          <div className="mb-6 flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            <div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          <DataTable
            data={filteredOrders}
            columns={columns}
            searchable={false}
            sortable={true}
          />
        </Card>
      </div>
    </div>
  );
};

export default Orders;